/**
 * Bezel sizing.
 *
 * Computes the largest 16:9 box that fits inside `.screen` (the cabinet's
 * center grid row, minus its padding) and writes the result to `.bezel`
 * as inline `width` + `height` styles. Re-runs on every viewport
 * resize, on devicePixelRatio changes, and when the cabinet flips
 * between attract/playing modes (which collapses the marquee + control
 * panel rows and changes the screen row's available height).
 *
 * Why JS instead of CSS? We tried four CSS-only sizing configs with
 * `aspect-ratio + max-*` and each one failed at some viewport aspect:
 * Chrome's aspect-ratio engine refuses to back-derive a dimension when
 * the other axis has both an explicit size AND a max-* set. Rather
 * than ship a sizing pattern that breaks at 30% of viewport aspects,
 * we compute the exact pixel size ourselves. Runs once per resize —
 * cost is negligible.
 *
 * The compositor's own ResizeObserver on the visible canvas picks up
 * the new size automatically and re-syncs the drawing buffer.
 *
 * Lifecycle:
 *   - `attachBezelFit()` once at boot. Wires resize listeners + computes
 *     the initial fit.
 *   - `recomputeBezelFit()` for explicit triggers — call after
 *     `setCabinetMode()` flips chrome on/off (changes the screen row
 *     height before the next animation frame).
 *   - `detachBezelFit()` for teardown (production never calls; exported
 *     for tests).
 */

const ASPECT_W = 16;
const ASPECT_H = 9;

let bezelEl: HTMLElement | null = null;
let screenEl: HTMLElement | null = null;
let observer: ResizeObserver | null = null;

/**
 * Compute and apply the bezel's pixel size. Largest 16:9 box that fits
 * inside the `.screen` content-box (clientWidth/clientHeight minus
 * padding; clientWidth is padding-box, not content-box, so we subtract
 * explicitly).
 *
 * Idempotent — only writes when values changed.
 */
export function recomputeBezelFit(): void {
  if (!bezelEl || !screenEl) return;

  // Read the screen row's actual content-box. Subtract padding
  // because clientWidth returns the padding-box width.
  const cs = getComputedStyle(screenEl);
  const padX =
    parseFloat(cs.paddingLeft || '0') + parseFloat(cs.paddingRight || '0');
  const padY =
    parseFloat(cs.paddingTop || '0') + parseFloat(cs.paddingBottom || '0');
  const availW = screenEl.clientWidth - padX;
  const availH = screenEl.clientHeight - padY;
  if (availW <= 0 || availH <= 0) return;

  // Largest 16:9 box that fits inside (availW, availH).
  const heightFromWidth = (availW * ASPECT_H) / ASPECT_W;
  let w: number;
  let h: number;
  if (heightFromWidth <= availH) {
    // Width is the constraint — full width, derived height.
    w = availW;
    h = heightFromWidth;
  } else {
    // Height is the constraint — full height, derived width.
    h = availH;
    w = (availH * ASPECT_W) / ASPECT_H;
  }

  // Round to whole pixels to avoid sub-pixel rendering artifacts.
  w = Math.floor(w);
  h = Math.floor(h);

  if (bezelEl.style.width !== `${w}px`) {
    bezelEl.style.width = `${w}px`;
  }
  if (bezelEl.style.height !== `${h}px`) {
    bezelEl.style.height = `${h}px`;
  }
}

/**
 * Wire up. Locates `.bezel` + `.screen`, attaches a ResizeObserver on
 * the screen row, and computes the initial fit.
 */
export function attachBezelFit(): void {
  detachBezelFit();
  const bezel = document.querySelector<HTMLElement>('.bezel');
  const screen = document.querySelector<HTMLElement>('.screen');
  if (!bezel || !screen) {
    console.warn('[bezel-fit] .bezel or .screen missing — skipping sizing');
    return;
  }
  bezelEl = bezel;
  screenEl = screen;
  recomputeBezelFit();

  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(() => recomputeBezelFit());
    observer.observe(screen);
  }
  // Belt-and-suspenders: ResizeObserver covers element-size changes;
  // window resize covers DPR shifts that the observer can miss
  // (dragging the browser window between monitors with different
  // pixel densities).
  window.addEventListener('resize', recomputeBezelFit);
}

/**
 * Tear down. Test-friendly; production never calls this.
 */
export function detachBezelFit(): void {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  window.removeEventListener('resize', recomputeBezelFit);
  bezelEl = null;
  screenEl = null;
}
